import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { computeChauffeurSplit, driverRateFor, driverPayFor, bookingHours, num } from "../lib/chauffeurSplit";

const money = (n) => new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(Number(n || 0));
const sel = "w-full bg-input border border-border rounded-md px-3 py-2 text-sm";

const ChauffeurSplitSection = forwardRef(function ChauffeurSplitSection({ booking, onSaved }, ref) {
  const [vehicle, setVehicle] = useState(null);
  const [driver, setDriver] = useState(null);
  const [autoFilled, setAutoFilled] = useState(false);
  const [form, setForm] = useState({
    driver_cost: booking.driver_cost || 0,
    gas_cost: booking.gas_cost || 0,
    other_costs: booking.other_costs || 0,
    deposit_held_by: booking.deposit_held_by || "company",
    balance_held_by: booking.balance_held_by || "company",
    driver_is_owner: booking.driver_is_owner || false,
    owner_drove: booking.owner_drove || false,
    settlement_override: booking.settlement_override || false,
    override_driver_direction: booking.override_driver_direction || "driver_owes",
    override_driver_amount: booking.override_driver_amount || 0,
    override_owner_direction: booking.override_owner_direction || "company_owes_owner",
    override_owner_amount: booking.override_owner_amount || 0,
    settlement_override_notes: booking.settlement_override_notes || "",
  });
  const [saving, setSaving] = useState(false);
  const dirtyRef = useRef(false);
  const formRef = useRef(form);
  formRef.current = form;
  const set = (k, v) => { dirtyRef.current = true; setForm((f) => ({ ...f, [k]: v })); };

  useEffect(() => {
    if (!booking.vehicle_id) return;
    api.get("/vehicles").then((r) => {
      setVehicle((r.data || []).find((v) => v.id === booking.vehicle_id) || null);
    }).catch(() => {});
  }, [booking.vehicle_id]);

  // Load the driver + auto-fill driver cost as hours × the driver's hourly
  // rate when it's blank (skipped once the split has been confirmed).
  useEffect(() => {
    if (!booking.driver_id) return;
    api.get("/chauffeurs").then((r) => {
      const d = (r.data || []).find((x) => x.id === booking.driver_id) || null;
      setDriver(d);
      const pay = driverPayFor(booking, d);
      if (pay > 0 && !booking.split_confirmed && !num(booking.driver_cost)) {
        setForm((f) => (num(f.driver_cost) > 0 ? f : { ...f, driver_cost: pay }));
        setAutoFilled(true);
        dirtyRef.current = true;
      }
    }).catch(() => {});
  }, [booking.driver_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Let the parent (BookingDetail) flush unsaved changes before navigating
  // to the previous/next booking.
  useImperativeHandle(ref, () => ({
    saveIfDirty: async () => {
      if (!dirtyRef.current) return true;
      try {
        await api.put(`/bookings/${booking.id}`, { ...booking, ...formRef.current });
        dirtyRef.current = false;
        toast.success("Split details saved");
        return true;
      } catch (e) {
        toast.error("Failed to save split details");
        return false;
      }
    },
  }));

  const s = computeChauffeurSplit({ ...booking, ...form }, vehicle, driver);
  const {
    total, deposit, balance, driverCost, netProfit, totalCosts, isThirdParty, isBaseRate,
    splitPct, splitType, ownerShare, companyShare, ownerTotalTake, moneyCompany, moneyDriver,
    driverEntitlement, driverNet,
  } = s;
  const ownerDrove = !!form.owner_drove;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/bookings/${booking.id}`, { ...booking, ...form });
      dirtyRef.current = false;
      toast.success("Split & money details saved");
      onSaved && onSaved();
    } catch (e) {
      toast.error("Failed to save");
    } finally { setSaving(false); }
  };

  const toggleConfirmed = async (next) => {
    setSaving(true);
    try {
      const payload = {
        ...booking,
        ...form,
        split_confirmed: next,
        split_confirmed_at: next ? new Date().toISOString() : "",
        split_confirmed_by: next ? (booking.split_confirmed_by || "") : "",
      };
      await api.put(`/bookings/${booking.id}`, payload);
      dirtyRef.current = false;
      toast.success(next ? "Split marked as confirmed" : "Confirmation removed");
      onSaved && onSaved();
    } catch (e) {
      toast.error("Failed to update confirmation");
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-border bg-card p-6" data-testid="chauffeur-split-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="label-mono text-primary">Ownership, Costs & Profit Split (admin only)</div>
        {booking.split_confirmed ? (
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs bg-emerald-500/15 text-emerald-500 border border-emerald-500/30" data-testid="split-confirmed-badge">
            ✓ Split confirmed{booking.split_confirmed_at ? ` · ${new Date(booking.split_confirmed_at).toLocaleDateString()}` : ""}
          </span>
        ) : (
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs bg-amber-500/10 text-amber-500 border border-amber-500/30" data-testid="split-unconfirmed-badge">
            ⚠ Split not yet confirmed
          </span>
        )}
      </div>

      {/* Ownership */}
      <div className="mb-5 text-sm">
        <span className="text-muted-foreground">Vehicle owner: </span>
        {isThirdParty
          ? <b data-testid="split-owner">{vehicle?.owner_name || "3rd party"} — {isBaseRate ? `base rate (${money(ownerShare)} this trip)` : `${splitPct}% of ${splitType === "percent_total" ? "total (gross)" : "net profit"}`}</b>
          : <b data-testid="split-owner">Company-owned (no split)</b>}
      </div>

      {/* Editable costs */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-5">
        <div className="space-y-1">
          <Label className="label-mono">Driver Cost</Label>
          <Input type="number" value={form.driver_cost} onChange={(e) => set("driver_cost", e.target.value)} data-testid="split-driver-cost" />
          <p className="text-[11px] text-muted-foreground">
            {autoFilled && num(form.driver_cost) === driverPayFor(booking, driver)
              ? <span className="text-primary">Auto: {Math.round(bookingHours(booking) * 10) / 10 || 1}h × {money(driverRateFor(booking, driver))}/h ({driver?.first_name || "driver"}&apos;s rate) — adjust or set 0 if included in the split</span>
              : "Set to 0 if included in the split"}
          </p>
        </div>
        <div className="space-y-1"><Label className="label-mono">Gas Cost</Label><Input type="number" value={form.gas_cost} onChange={(e) => set("gas_cost", e.target.value)} data-testid="split-gas-cost" /></div>
        <div className="space-y-1"><Label className="label-mono">Additional Costs</Label><Input type="number" value={form.other_costs} onChange={(e) => set("other_costs", e.target.value)} data-testid="split-additional-cost" /></div>
      </div>

      {/* Money holding selectors */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-5">
        <div className="space-y-1">
          <Label className="label-mono">Deposit held by ({money(deposit)})</Label>
          <select className={sel} value={form.deposit_held_by} onChange={(e) => set("deposit_held_by", e.target.value)} data-testid="split-deposit-held-by">
            <option value="company">Us (Company)</option>
            <option value="driver">Driver</option>
          </select>
        </div>
        <div className="space-y-1">
          <Label className="label-mono">Balance held by ({money(balance)})</Label>
          <select className={sel} value={form.balance_held_by} onChange={(e) => set("balance_held_by", e.target.value)} data-testid="split-balance-held-by">
            <option value="company">Us (Company)</option>
            <option value="driver">Driver (cash)</option>
          </select>
        </div>
        <div className="space-y-1">
          <Label className="label-mono">Driver is the 3rd-party owner?</Label>
          <select className={sel} value={form.driver_is_owner ? "yes" : "no"} onChange={(e) => set("driver_is_owner", e.target.value === "yes")} data-testid="split-driver-is-owner" disabled={!isThirdParty}>
            <option value="no">No</option>
            <option value="yes">Yes — owner drives this car</option>
          </select>
        </div>
      </div>

      {/* Owner drove this booking toggle */}
      <div className="flex items-center justify-between gap-4 border border-border rounded-md px-4 py-3 mb-5">
        <div>
          <Label className="cursor-pointer">Driver drove this booking & keeps the driver fee</Label>
          <p className="text-[11px] text-muted-foreground mt-0.5">On = the driver fee ({money(driverCost)}) is paid to the owner/driver on top of their split (for % owned cars).</p>
        </div>
        <Switch checked={form.owner_drove} onCheckedChange={(v) => set("owner_drove", v)} data-testid="split-owner-drove" />
      </div>

      {/* Calculations */}
      <div className="border border-border rounded-md p-4 bg-secondary/30 text-sm space-y-1.5" data-testid="split-summary">
        {booking.tax_applied && <Row label="Client pays incl. HST (13%)" value={money(total * 1.13)} />}
        <Row label={booking.tax_applied ? "Pre-tax total (split math uses this)" : "Client total"} value={money(total)} />
        <Row label="− Total costs (driver + gas + additional)" value={`− ${money(totalCosts)}`} />
        <Row label="Net profit" value={money(netProfit)} bold />
        {isThirdParty && <Row label={isBaseRate ? "Owner base rate" : `Owner split (${splitPct}%)`} value={money(ownerShare)} />}
        {isThirdParty && ownerDrove && !isBaseRate && <Row label="+ Driver fee kept by owner (owner drove)" value={`+ ${money(driverCost)}`} />}
        {isThirdParty && ownerDrove && !isBaseRate && <Row label="Owner ends up with" value={money(ownerTotalTake)} bold />}
        <Row label="Company share" value={money(companyShare)} />
        <div className="border-t border-border my-2" />
        <Row label="Money held by Us" value={money(moneyCompany)} />
        <Row label="Money held by Driver" value={money(moneyDriver)} />
        <Row label={`Driver should end with (fee + gas/additional fronted${form.driver_is_owner ? " + owner split)" : ")"}`} value={money(driverEntitlement)} />
      </div>

      {/* Who owes who */}
      <div className="mt-4 space-y-2">
        {form.settlement_override ? (
          <>
            {form.override_driver_direction === "driver_owes" && Number(form.override_driver_amount) > 0 && (
              <Banner color="amber" testid="split-settle">Driver owes Company <b>{money(form.override_driver_amount)}</b> <span className="text-xs opacity-80">(manual override)</span></Banner>
            )}
            {form.override_driver_direction === "company_owes_driver" && Number(form.override_driver_amount) > 0 && (
              <Banner color="blue" testid="split-settle">Company owes Driver <b>{money(form.override_driver_amount)}</b> <span className="text-xs opacity-80">(manual override)</span></Banner>
            )}
            {form.override_driver_direction === "settled" && (
              <Banner color="green" testid="split-settle">Driver ↔ Company settled <span className="text-xs opacity-80">(manual override)</span></Banner>
            )}
            {isThirdParty && form.override_owner_direction === "company_owes_owner" && Number(form.override_owner_amount) > 0 && (
              <Banner color="zinc" testid="split-owner-payable">Company owes owner {vehicle?.owner_name || "(owner)"} <b>{money(form.override_owner_amount)}</b> <span className="text-xs opacity-80">(manual override)</span></Banner>
            )}
            {isThirdParty && form.override_owner_direction === "owner_owes" && Number(form.override_owner_amount) > 0 && (
              <Banner color="amber" testid="split-owner-payable">Owner {vehicle?.owner_name || "(owner)"} owes Company <b>{money(form.override_owner_amount)}</b> <span className="text-xs opacity-80">(manual override)</span></Banner>
            )}
            {isThirdParty && form.override_owner_direction === "settled" && (
              <Banner color="green" testid="split-owner-payable">Owner {vehicle?.owner_name || "(owner)"} ↔ Company settled <span className="text-xs opacity-80">(manual override)</span></Banner>
            )}
          </>
        ) : (
          <>
            {driverNet > 0 && <Banner color="amber" testid="split-settle">Driver owes Company <b>{money(driverNet)}</b></Banner>}
            {driverNet < 0 && <Banner color="blue" testid="split-settle">Company owes Driver <b>{money(Math.abs(driverNet))}</b></Banner>}
            {driverNet === 0 && <Banner color="green" testid="split-settle">Settled — driver is holding exactly what they&apos;re owed.</Banner>}
            {isThirdParty && !form.driver_is_owner && ownerShare !== 0 && (
              <Banner color="zinc" testid="split-owner-payable">Company owes owner {vehicle?.owner_name || "(owner)"} <b>{money(ownerTotalTake)}</b> (profit split{ownerDrove && !isBaseRate ? " + driver fee" : ""})</Banner>
            )}
          </>
        )}
      </div>

      {/* Manual override — admin can hand-set who-owes-who and add a note */}
      <div className="mt-6 border border-border rounded-md p-4 bg-secondary/20" data-testid="split-override-section">
        <div className="flex items-center justify-between gap-4 mb-3">
          <div>
            <Label className="cursor-pointer">Manual override — set who owes who by hand</Label>
            <p className="text-[11px] text-muted-foreground mt-0.5">Use when a trip settled outside the usual math (cash tip, comp&apos;d, made-good on a prior trip, etc.). Overrides the banners above and the 3rd-Party Owners running balance.</p>
          </div>
          <Switch checked={form.settlement_override} onCheckedChange={(v) => set("settlement_override", v)} data-testid="split-override-toggle" />
        </div>

        {form.settlement_override && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="label-mono">Driver ↔ Company</Label>
                <select className={sel} value={form.override_driver_direction} onChange={(e) => set("override_driver_direction", e.target.value)} data-testid="split-override-driver-dir">
                  <option value="driver_owes">Driver owes Company</option>
                  <option value="company_owes_driver">Company owes Driver</option>
                  <option value="settled">Settled (no money moves)</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label className="label-mono">Amount</Label>
                <Input type="number" value={form.override_driver_amount} onChange={(e) => set("override_driver_amount", e.target.value)} disabled={form.override_driver_direction === "settled"} data-testid="split-override-driver-amt" />
              </div>
            </div>

            {isThirdParty && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="label-mono">Owner ({vehicle?.owner_name || "3rd-party"}) ↔ Company</Label>
                  <select className={sel} value={form.override_owner_direction} onChange={(e) => set("override_owner_direction", e.target.value)} data-testid="split-override-owner-dir">
                    <option value="company_owes_owner">Company owes Owner</option>
                    <option value="owner_owes">Owner owes Company</option>
                    <option value="settled">Settled (no money moves)</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="label-mono">Amount</Label>
                  <Input type="number" value={form.override_owner_amount} onChange={(e) => set("override_owner_amount", e.target.value)} disabled={form.override_owner_direction === "settled"} data-testid="split-override-owner-amt" />
                </div>
              </div>
            )}

            <div className="space-y-1">
              <Label className="label-mono">Reason / notes</Label>
              <textarea
                className={`${sel} min-h-[80px] resize-y`}
                value={form.settlement_override_notes}
                onChange={(e) => set("settlement_override_notes", e.target.value)}
                placeholder="Why is this trip settled differently? (e.g. driver kept a $50 cash tip · comp'd for a friend · balances out last week's short-pay…)"
                data-testid="split-override-notes"
              />
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-end gap-2 flex-wrap">
        {booking.split_confirmed ? (
          <Button variant="ghost" onClick={() => toggleConfirmed(false)} disabled={saving} data-testid="split-unconfirm">
            Undo confirmation
          </Button>
        ) : (
          <Button variant="outline" onClick={() => toggleConfirmed(true)} disabled={saving} className="border-emerald-500/50 text-emerald-500 hover:bg-emerald-500/10" data-testid="split-confirm">
            ✓ Confirm split details
          </Button>
        )}
        <Button onClick={save} disabled={saving} data-testid="split-save">Save split & money details</Button>
      </div>
    </div>
  );
});

export default ChauffeurSplitSection;

function Row({ label, value, bold }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={bold ? "font-heading" : "font-mono-data"}>{value}</span>
    </div>
  );
}

function Banner({ color, children, testid }) {
  const map = {
    amber: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    blue: "bg-blue-500/15 text-blue-500 border-blue-500/30",
    green: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
    zinc: "bg-secondary text-foreground border-border",
  };
  return <div className={`border rounded-md px-4 py-2.5 text-sm ${map[color]}`} data-testid={testid}>{children}</div>;
}
